import React, { useState, useMemo, useCallback, memo, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ModalGerarEstrutura from './ModalGerarEstrutura';
import './TopMenu.css';

const MenuItem = memo(({ item, isActive, onClick }) => (
  <Link
    to={item.path}
    className={`top-menu-item ${isActive ? 'active' : ''}`}
    onClick={onClick}
    title={item.label}
  >
    <span className="top-menu-icon" role="img" aria-label={item.label}>{item.icon}</span>
    <span className="top-menu-label">{item.label}</span>
  </Link>
));

const DropdownItem = memo(({ item, isActive, onClick }) => {
  const handleClick = (e) => {
    // Permitir que o Link faça a navegação normalmente
    if (onClick) {
      onClick(e);
    }
  };
  
  return (
    <Link
      to={item.path}
      className={`dropdown-item ${isActive ? 'active' : ''}`}
      onClick={handleClick}
      title={item.label}
    >
      <span className="dropdown-icon" role="img" aria-label={item.label}>{item.icon}</span>
      <span className="dropdown-label">{item.label}</span>
    </Link>
  );
});

const TopMenu = () => {
  const location = useLocation();
  const { user } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [adminDropdownOpen, setAdminDropdownOpen] = useState(false);
  const [documentosDropdownOpen, setDocumentosDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [clickedDropdown, setClickedDropdown] = useState(null); // 'documentos', 'cadastro', 'admin'
  const [showModalGerarEstrutura, setShowModalGerarEstrutura] = useState(false);
  const isAdmin = user?.tipo_usuario === 'admin';
  const isGestor = user?.nivel_permissao >= 2 || user?.tipo_usuario === 'gestor';
  const podeVerAdmin = isAdmin || isGestor; // Admin ou gestor podem ver menu de administração

  const menuItems = useMemo(() => [
    { path: '/', label: 'Dashboard', icon: '📊' },
    { 
      path: '/documentos', 
      label: 'Documentos', 
      icon: '📄',
      submenu: [
        { path: '/documentos/enviados', label: 'Enviados', icon: '📤' },
        { path: '/documentos', label: 'Recebidos', icon: '📥' },
        { path: '/documentos/criar', label: 'Criar Documentos', icon: '➕' }
      ]
    },
    { path: '/consulta', label: 'Consulta', icon: '🔍' },
  ], []);

  const cadastroItems = useMemo(() => [
    { path: '/usuarios', label: 'Usuários', icon: '👥' },
    { path: '/cidades', label: 'Cidades', icon: '🏙️' },
    { path: '/secretarias', label: 'Secretarias', icon: '🏛️' },
    { path: '/subsecretarias', label: 'Subsecretarias', icon: '📋' },
    { path: '/tipos-documentos', label: 'Tipos de Documentos', icon: '📑' },
    { path: '/certificados', label: 'Meu Certificado', icon: '🔐' },
  ], []);

  const adminItems = useMemo(() => [
    { path: '/admin/certificados', label: 'Gerenciar Certificados', icon: '🔐' },
    { path: '/workflow', label: 'Gerenciar Workflow', icon: '⚙️' },
  ], []);

  const isCadastroActive = useMemo(
    () => cadastroItems.some(item => location.pathname === item.path),
    [cadastroItems, location.pathname]
  );

  const isAdminActive = useMemo(
    () => adminItems.some(item => location.pathname === item.path),
    [adminItems, location.pathname]
  );

  const isDocumentosActive = useMemo(
    () => location.pathname.startsWith('/documentos'),
    [location.pathname]
  );

  const handleDropdownEnter = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + 4,
      left: rect.left
    });
    setDropdownOpen(true);
  }, []);

  const handleDropdownLeave = useCallback(() => {
    // Só fecha se não foi clicado
    if (clickedDropdown !== 'cadastro') {
      setDropdownOpen(false);
    }
  }, [clickedDropdown]);

  const handleDropdownClick = useCallback((e, type) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + 4,
      left: rect.left
    });
    
    if (clickedDropdown === type) {
      // Se já está aberto, fecha
      setClickedDropdown(null);
      if (type === 'documentos') setDocumentosDropdownOpen(false);
      if (type === 'cadastro') setDropdownOpen(false);
      if (type === 'admin') setAdminDropdownOpen(false);
    } else {
      // Abre o novo e fecha os outros
      setClickedDropdown(type);
      if (type === 'documentos') {
        setDocumentosDropdownOpen(true);
        setDropdownOpen(false);
        setAdminDropdownOpen(false);
      } else if (type === 'cadastro') {
        setDropdownOpen(true);
        setDocumentosDropdownOpen(false);
        setAdminDropdownOpen(false);
      } else if (type === 'admin') {
        setAdminDropdownOpen(true);
        setDropdownOpen(false);
        setDocumentosDropdownOpen(false);
      }
    }
  }, [clickedDropdown]);
  
  // Garantir que o dropdown abra ao clicar mesmo se já estiver aberto
  useEffect(() => {
    if (clickedDropdown === 'documentos' && !documentosDropdownOpen) {
      setDocumentosDropdownOpen(true);
    }
  }, [clickedDropdown, documentosDropdownOpen]);

  const handleDropdownItemClick = useCallback(() => {
    setDropdownOpen(false);
    setDocumentosDropdownOpen(false);
    setAdminDropdownOpen(false);
    setClickedDropdown(null);
  }, []);

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (clickedDropdown && !e.target.closest('.top-menu-dropdown') && !e.target.closest('.dropdown-menu')) {
        setClickedDropdown(null);
        setDropdownOpen(false);
        setDocumentosDropdownOpen(false);
        setAdminDropdownOpen(false);
      }
    };

    if (clickedDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [clickedDropdown]);

  return (
    <div className="top-menu">
      <div className="top-menu-content">
        {menuItems.map((item) => {
          if (item.submenu) {
            return (
              <div
                key={item.path}
                className={`top-menu-dropdown ${documentosDropdownOpen ? 'open' : ''} ${isDocumentosActive ? 'active' : ''}`}
                onMouseEnter={(e) => {
                  if (clickedDropdown !== 'documentos') {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setDropdownPosition({
                      top: rect.bottom + 4,
                      left: rect.left
                    });
                    setDocumentosDropdownOpen(true);
                  }
                }}
                onMouseLeave={(e) => {
                  if (clickedDropdown !== 'documentos') {
                    const relatedTarget = e.relatedTarget;
                    if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
                      setDocumentosDropdownOpen(false);
                    }
                  }
                }}
              >
                <div 
                  className="top-menu-item dropdown-trigger" 
                  title={item.label}
                  onClick={(e) => handleDropdownClick(e, 'documentos')}
                >
                  <span className="top-menu-icon" role="img" aria-label={item.label}>{item.icon}</span>
                  <span className="top-menu-label">{item.label}</span>
                  <span className="dropdown-arrow" aria-hidden="true">▼</span>
                </div>
                
                {documentosDropdownOpen && (
                  <div 
                    className="dropdown-menu floating-menu" 
                    style={{ left: `${dropdownPosition.left}px`, top: `${dropdownPosition.top}px` }}
                    onMouseEnter={() => setDocumentosDropdownOpen(true)}
                    onMouseLeave={() => {
                      if (clickedDropdown !== 'documentos') {
                        setDocumentosDropdownOpen(false);
                      }
                    }}
                  >
                    {item.submenu.map((subItem) => {
                      // Lógica para determinar se está ativo
                      let isItemActive = false;
                      if (subItem.path === '/documentos') {
                        // Recebidos: ativo se for /documentos ou /documentos/ mas não /enviados ou /criar
                        isItemActive = location.pathname === '/documentos' || 
                          (location.pathname.startsWith('/documentos') && 
                           !location.pathname.includes('/enviados') && 
                           !location.pathname.includes('/criar'));
                      } else {
                        // Enviados ou Criar: ativo se o pathname corresponder
                        isItemActive = location.pathname === subItem.path || 
                          location.pathname.startsWith(subItem.path + '/');
                      }
                      
                      return (
                        <DropdownItem
                          key={subItem.path}
                          item={subItem}
                          isActive={isItemActive}
                          onClick={() => {
                            handleDropdownItemClick();
                            setDocumentosDropdownOpen(false);
                            setClickedDropdown(null);
                          }}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }
          return (
          <MenuItem
            key={item.path}
            item={item}
            isActive={location.pathname === item.path}
          />
          );
        })}

        {isAdmin && (
          <div 
            className={`top-menu-dropdown ${dropdownOpen ? 'open' : ''} ${isCadastroActive ? 'active' : ''}`}
            onMouseEnter={handleDropdownEnter}
            onMouseLeave={(e) => {
              if (clickedDropdown !== 'cadastro') {
                const relatedTarget = e.relatedTarget;
                if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
                  handleDropdownLeave();
                }
              }
            }}
          >
            <div 
              className="top-menu-item dropdown-trigger" 
              title="Cadastro"
              onClick={(e) => handleDropdownClick(e, 'cadastro')}
            >
              <span className="top-menu-icon" role="img" aria-label="Cadastro">📝</span>
              <span className="top-menu-label">Cadastro</span>
              <span className="dropdown-arrow" aria-hidden="true">▼</span>
            </div>
            
            {dropdownOpen && (
              <div 
                className="dropdown-menu floating-menu" 
                style={{ left: `${dropdownPosition.left}px`, top: `${dropdownPosition.top}px` }}
                onMouseEnter={handleDropdownEnter}
                onMouseLeave={() => {
                  if (clickedDropdown !== 'cadastro') {
                    handleDropdownLeave();
                  }
                }}
              >
                {cadastroItems.filter(item => item.path !== '/certificados').map((item) => (
                  <DropdownItem
                    key={item.path}
                    item={item}
                    isActive={location.pathname === item.path}
                    onClick={() => {
                      handleDropdownItemClick();
                      setDropdownOpen(false);
                      setClickedDropdown(null);
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Certificados sempre visível para todos */}
        {!isAdmin && (
          <MenuItem
            item={{ path: '/certificados', label: 'Meu Certificado', icon: '🔐' }}
            isActive={location.pathname === '/certificados'}
          />
        )}

        {podeVerAdmin && (
          <div 
            className={`top-menu-dropdown ${adminDropdownOpen ? 'open' : ''} ${isAdminActive ? 'active' : ''}`}
            onMouseEnter={(e) => {
              if (clickedDropdown !== 'admin') {
                const rect = e.currentTarget.getBoundingClientRect();
                setDropdownPosition({
                  top: rect.bottom + 4,
                  left: rect.left
                });
                setAdminDropdownOpen(true);
              }
            }}
            onMouseLeave={(e) => {
              if (clickedDropdown !== 'admin') {
                const relatedTarget = e.relatedTarget;
                if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
                  setAdminDropdownOpen(false);
                }
              }
            }}
          >
            <div 
              className="top-menu-item dropdown-trigger" 
              title="Administração"
              onClick={(e) => handleDropdownClick(e, 'admin')}
            >
              <span className="top-menu-icon" role="img" aria-label="Administração">⚙️</span>
              <span className="top-menu-label">Administração</span>
              <span className="dropdown-arrow" aria-hidden="true">▼</span>
            </div>
            
            {adminDropdownOpen && (
              <div 
                className="dropdown-menu floating-menu" 
                style={{ left: `${dropdownPosition.left}px`, top: `${dropdownPosition.top}px` }}
                onMouseEnter={() => setAdminDropdownOpen(true)}
                onMouseLeave={() => {
                  if (clickedDropdown !== 'admin') {
                    setAdminDropdownOpen(false);
                  }
                }}
              >
                {/* Renderizar itens do admin */}
                {adminItems && Array.isArray(adminItems) && adminItems.length > 0 ? (
                  adminItems.map((item, index) => (
                    <DropdownItem
                      key={item.path || `admin-item-${index}`}
                      item={item}
                      isActive={location.pathname === item.path || (item.path && location.pathname.startsWith(item.path + '/'))}
                      onClick={() => {
                        handleDropdownItemClick();
                        setAdminDropdownOpen(false);
                        setClickedDropdown(null);
                      }}
                    />
                  ))
                ) : (
                  <div className="dropdown-item">
                    <span className="dropdown-label">Nenhum item disponível</span>
                  </div>
                )}
                
                {/* Botão Gerar Estrutura - apenas para admin */}
                {isAdmin && (
                  <button
                    className="dropdown-item dropdown-button"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowModalGerarEstrutura(true);
                      setAdminDropdownOpen(false);
                      setClickedDropdown(null);
                    }}
                  >
                    <span className="dropdown-icon" role="img" aria-label="Gerar Estrutura">📁</span>
                    <span className="dropdown-label">Gerar Estrutura</span>
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal Gerar Estrutura */}
      <ModalGerarEstrutura
        isOpen={showModalGerarEstrutura}
        onClose={() => setShowModalGerarEstrutura(false)}
        onSuccess={() => {
          // Recarregar página ou atualizar TreeView se necessário
          window.location.reload();
        }}
      />
    </div>
  );
};

export default memo(TopMenu);
